import { DELIVERY_STATE_KEY, EMIRATES, restoreDeliverySelection, selectDeliveryEmirate, incompleteDeliveryAttributes } from '@theme/rdb-delivery-rules';
import { CartUpdateEvent } from '@theme/events';

class RosariumEmiratePicker extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.config = JSON.parse(this.querySelector('[data-emirate-config]').textContent);
    this.root = this.config.root.replace(/\/?$/, '/');
    this.clockBase = this.config.serverTime;
    this.clockStart = performance.now();
    this.trigger = this.querySelector('.rdb-emirate__trigger');
    this.panel = this.querySelector('.rdb-emirate__panel');
    this.feedback = this.querySelector('.rdb-emirate__feedback');
    this.retry = this.querySelector('[data-emirate-retry]');
    this.state = this.restore(this.config.selection);
    this.revision = 0;
    this.trigger.addEventListener('click', () => this.setOpen(this.panel.hidden), options);
    this.addEventListener('change', (event) => {
      if (event.target.matches('[name=header_emirate]')) this.choose(event.target.value);
    }, options);
    this.retry.addEventListener('click', () => this.choose(this.pendingEmirate || this.state.emirate), options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) { event.preventDefault(); event.stopPropagation(); this.setOpen(false, true); }
    }, options);
    document.addEventListener('pointerdown', (event) => { if (!this.contains(event.target)) this.setOpen(false); }, options);
    document.addEventListener('focusin', (event) => { if (!this.contains(event.target)) this.setOpen(false); }, options);
    document.addEventListener('rdb:delivery-selection', (event) => {
      this.pendingEmirate = event.detail.state.emirate;
      this.render(event.detail);
    }, options);
    document.addEventListener('rdb:delivery-saved', () => this.publish(), options);
    window.addEventListener('pageshow', () => this.refresh(), options);
    window.addEventListener('focus', () => this.refresh(), options);
    window.addEventListener('storage', (event) => { if (event.key === 'rdb-delivery-updated') this.refresh(); }, options);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
    this.querySelector('.rdb-emirate__fallback').hidden = true;
    this.trigger.hidden = false;
    this.render({ state: this.state });
    this.refresh();
  }

  disconnectedCallback() { this.abort?.abort(); }
  controller() { const delivery = document.querySelector('rdb-delivery'); return delivery?.config && typeof delivery.setEmirate === 'function' ? delivery : null; }
  restore(value) { return restoreDeliverySelection(value, new Date(this.clockBase + performance.now() - this.clockStart), this.config.rules); }
  publish() { try { localStorage.setItem('rdb-delivery-updated', `${Date.now()}-${Math.random()}`); } catch { /* Cart remains canonical if storage is unavailable. */ } }

  setOpen(open, focus = false) {
    this.panel.hidden = !open;
    this.trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      (this.querySelector('input:checked') || this.querySelector('input'))?.focus({ preventScroll: true });
    } else if (focus) this.trigger.focus({ preventScroll: true });
  }

  render({ state, saving = false, failed = false, checkingOut = false }) {
    this.state = { ...state };
    // One document state also covers cards inserted by native pagination.
    document.documentElement.dataset.rdbEmirate = state.emirate;
    this.busy = saving || checkingOut;
    this.failed = failed;
    const selected = [...this.querySelectorAll('input')].find(input => input.value === state.emirate);
    const label = selected?.nextElementSibling.textContent || this.config.strings.empty;
    this.querySelector('[data-emirate-label]').textContent = label;
    this.trigger.title = label;
    this.trigger.disabled = checkingOut;
    this.trigger.setAttribute('aria-busy', String(saving));
    this.trigger.setAttribute('aria-label', saving ? this.config.strings.saving : selected ? this.config.strings.selected.replace('__EMIRATE__', label) : this.config.strings.choose);
    this.querySelector('fieldset').disabled = this.busy;
    for (const input of this.querySelectorAll('input')) input.checked = input.value === state.emirate;
    this.feedback.textContent = failed ? this.config.strings.error : saving ? this.config.strings.saving : '';
    this.feedback.dataset.error = String(failed);
    this.retry.hidden = !failed;
    if (failed) this.setOpen(true);
  }

  async request(path, body) {
    const response = await fetch(this.root + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(15000),
    });
    const date = Date.parse(response.headers.get('date') || '');
    if (Number.isFinite(date)) { this.clockBase = date; this.clockStart = performance.now(); }
    if (!response.ok) throw new Error('request');
    const cart = await response.json();
    if (cart.status || cart.errors) throw new Error('cart');
    return cart;
  }

  async refresh() {
    if (this.busy || this.failed || this.refreshing) return;
    const controller = this.controller();
    if (controller) {
      this.render({ state: controller.state, saving: controller.saving, failed: controller.saveFailed, checkingOut: controller.checkingOut });
      return;
    }
    this.refreshing = true;
    const revision = this.revision;
    try {
      const cart = await this.request('cart.js');
      if (this.isConnected && revision === this.revision && !this.busy && !this.failed) this.render({ state: this.restore(cart.attributes[DELIVERY_STATE_KEY]) });
    } catch { /* Retain the server-rendered selection; changes have their own retry UI. */ }
    finally { this.refreshing = false; }
  }

  async choose(emirate) {
    if (this.busy || !EMIRATES.includes(emirate)) return;
    this.pendingEmirate = emirate;
    this.revision += 1;
    const controller = this.controller();
    if (controller) {
      if (controller.setEmirate(emirate)) this.setOpen(false, true);
      return;
    }
    const previous = { ...this.state };
    this.render({ state: selectDeliveryEmirate(this.state, emirate), saving: true });
    try {
      const cart = await this.request('cart.js');
      const current = this.restore(cart.attributes[DELIVERY_STATE_KEY]);
      // Selecting the existing destination must not discard its delivery window.
      if (current.emirate === emirate) {
        this.render({ state: current }); this.setOpen(false, true); this.publish(); return;
      }
      const state = selectDeliveryEmirate(current, emirate);
      const attributes = incompleteDeliveryAttributes(state);
      const feeIds = this.config.feeIds.filter(id => id > 0);
      const updates = Object.fromEntries(cart.items.filter(item => feeIds.includes(item.variant_id)).map(item => [item.key, 0]));
      const saved = await this.request('cart/update.js', { attributes, ...(Object.keys(updates).length ? { updates } : {}) });
      if (saved.attributes[DELIVERY_STATE_KEY] !== attributes[DELIVERY_STATE_KEY]) throw new Error('readback');
      this.render({ state }); this.setOpen(false, true); this.publish();
      if (Object.keys(updates).length) document.dispatchEvent(new CartUpdateEvent(saved, 'rdb-header-emirate', { source: 'rdb-header-emirate', itemCount: saved.item_count }));
    } catch {
      this.render({ state: previous, failed: true });
    }
  }
}

if (!customElements.get('rdb-emirate-picker')) customElements.define('rdb-emirate-picker', RosariumEmiratePicker);
